<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'

import { HeadingType } from '@xcel-next/core'

import { usePageStore } from '../../stores/page'
import { useImgSrcSet } from '../../composables/use-img-src-set'
import MetadataHeader from '../content-header/MetadataHeader.vue'
import ParagraphTypeVodStreamingOptions from './ParagraphTypeVodStreamingOptions.vue'
import AffiliateInfo from '../shared/AffiliateInfo.vue'

import type { VodStreamingCardParagraph } from '../../utils/paragraph-schema/paragraphs'

type MetaInfos = {
  country?: string
  year?: number
  genre?: string
  runtime?: number
}

const MAX_OFFERS_PER_CATEGORY = 6
const POSTER_SRC_SET_NAME = 'bx-vod-streaming-card'

const props = defineProps<{
  vodStreamingCard: VodStreamingCardParagraph['vodStreamingCard']
}>()

const { createImgSrcSetArray } = useImgSrcSet()
const { brandFromStore, isArticleOrArticleVersion } = storeToRefs(usePageStore())

const metaInfos = computed<MetaInfos | null>(() => {
  const info: MetaInfos = {}
  const card = props.vodStreamingCard
  const countries = card.productionCountries ?? []
  if (countries.length > 0) info.country = countries.join(', ')
  if (card.releaseYear) info.year = card.releaseYear
  // only the first genre - it carries the highest weighting, the rest are mostly sub-genres
  const genres = card.genres ?? []
  if (genres.length > 0) info.genre = genres[0]
  if (card.runtime) info.runtime = card.runtime
  return Object.keys(info).length > 0 ? info : null
})

const posterSrcSet = computed(() => {
  const poster = props.vodStreamingCard.poster
  if (!poster?.imageId) return []
  return createImgSrcSetArray(poster, brandFromStore.value, POSTER_SRC_SET_NAME, 'webp')
})

const posterFallbackSrc = computed(() => {
  const sources = posterSrcSet.value
  if (sources.length === 0) return undefined
  // widest generated src as the <img> fallback
  return sources.reduce((widest, current) => (
    srcWidth(current.src) > srcWidth(widest.src) ? current : widest
  ), sources[0]).src
})

const limitedStreamingOptions = computed(() => {
  const options = props.vodStreamingCard.streamingOptions
  if (!options) return null
  // offers arrive pre-sorted by provider priority - cap at max grid columns, CSS shows only what fits in one row
  return {
    free: (options.free ?? []).slice(0, MAX_OFFERS_PER_CATEGORY),
    flatrate: (options.flatrate ?? []).slice(0, MAX_OFFERS_PER_CATEGORY),
    buy: (options.buy ?? []).slice(0, MAX_OFFERS_PER_CATEGORY),
    rent: (options.rent ?? []).slice(0, MAX_OFFERS_PER_CATEGORY)
  }
})

// normalize CR/CRLF to LF: the browser rewrites carriage returns to \n while parsing, so raw \r from the API would break SSR hydration
const overview = computed(() => props.vodStreamingCard.overview?.replace(/\r\n?/g, '\n'))

function setMediaQuery (viewport: string): string {
  return `(max-width: ${viewport}px)`
}

function srcWidth (src: string | undefined): number {
  return Number(src?.match(/w=(\d+)/)?.[1] ?? 0)
}
</script>

<template>
  <div class="bx-content bx-vod-streaming-card bx-typo--teaser"
       :class="{ 'bx-vod-streaming-card--article': isArticleOrArticleVersion }"
       data-tc="vod-streaming-card">
    <div class="bx-vod-streaming-card__main">
      <a class="bx-vod-streaming-card__poster"
         :href="vodStreamingCard.url"
         target="_self"
         data-tc="vod-streaming-card-poster">
        <picture v-if="posterSrcSet.length > 0">
          <source v-for="(singleSrc, index) in posterSrcSet"
                  :key="index"
                  type="image/webp"
                  :media="setMediaQuery(singleSrc.viewportWidth)"
                  :srcset="singleSrc.src">
          <img :src="posterFallbackSrc"
               data-pin-nopin="true"
               data-tc="vod-streaming-card-poster-image"
               loading="lazy"
               :width="vodStreamingCard.poster.width"
               :height="vodStreamingCard.poster.height"
               :alt="`${vodStreamingCard.title} - Foto: ${vodStreamingCard.poster.imageCredit || 'TMDB'}`"
               :title="vodStreamingCard.title">
        </picture>
      </a>
      <a class="bx-vod-streaming-card__info bx-vod-streaming-card__title-link bx-typo--content-continuous-text"
         :href="vodStreamingCard.url"
         target="_self">
        <component :is="vodStreamingCard.heading === HeadingType.none ? 'div' : (vodStreamingCard.heading ?? HeadingType.h3)"
                   class="bx-vod-streaming-card__title bx-typo--h3"
                   data-tc="vod-streaming-card-title">
          {{ vodStreamingCard.title }}
        </component>
        <MetadataHeader v-if="metaInfos"
                        :meta-infos="metaInfos" />
        <p v-if="overview"
           class="bx-vod-streaming-card__overview"
           data-tc="vod-streaming-card-overview">
          {{ overview }}
        </p>
      </a>
    </div>
    <div v-if="limitedStreamingOptions"
         class="bx-vod-streaming-card__offers"
         data-tc="vod-streaming-card-offers">
      <AffiliateInfo affiliate-type="affiliate-link" />
      <ParagraphTypeVodStreamingOptions :vod-streaming-options="limitedStreamingOptions"
                                        avoid-headline-tag />
    </div>
  </div>
</template>
